import {
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import CardContent from '../common/card/CardContent';
import { useUpdateSignup } from '../login/Signup.hooks';
import { BaseOnboardingProps, Signup } from '../login/Signup.interface';

const StyledCard = styled(Card)`
  max-width: 640px;
  margin: 0 auto;
  position: relative;
  .image {
    margin-top: 16px;
    overflow: hidden;
    img {
      width: 60%;
      display: block;
      margin-top: -8%;
      margin-bottom: -3%;
      margin-left: auto;
      margin-right: auto;
    }
  }
  .description {
    margin-top: 12px;
  }
  .card-content {
    padding-left: 32px;
    padding-right: 32px;
    padding-bottom: 32px;
  }
  .form {
    /* display: flex;
    flex-direction: column; */
    .field {
      padding: 24px 0 8px 0;
    }
    .field-label {
      padding-bottom: 8px;
      font-weight: 500;
      font-size: 18px;
    }
    .caption {
      font-size: 14px;
      padding-bottom: 12px;
    }
  }
  .submit {
    margin-top: 32px;
  }
`;

type CompanyInfoFormValues = Pick<
  Signup,
  | 'industries'
  | 'hasExistingQms'
  | 'existingQmsInfo'
  | 'migrateExistingQms'
  | 'hasPolicies'
>;

export const industries = [
  'Medical Device',
  'Digital Health',
  'BioPharma',
  'CRO',
  'Healthcare',
  'Other'
];

const OnboardingCompanyInfo: React.FC<BaseOnboardingProps> = props => {
  const { signup, onContinue, onChange, onUpdate, onError } = props;
  const onChangeRef = useRef(onChange);
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const {
    signup: updatedSignup,
    isLoading,
    error,
    update
  } = useUpdateSignup(signup.id);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (updatedSignup) {
      onChangeRef.current?.(updatedSignup);
      onUpdateRef.current?.();
    }
  }, [updatedSignup]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <StyledCard>
      {/* {isLoading && <LinearProgress className='progress' />} */}
      <div className='image'>
        <img
          draggable='false'
          alt='Company Information'
          src='/assets/onboarding/company-info.png'
        />
      </div>
      <CardContent className='card-content'>
        <Typography variant='h2'>Company Information</Typography>
        <Typography variant='subtitle1' className='description'>
          Help us customize the Sierra Quality Management System (QMS) for you
          by providing the following information. This will allows us to provide
          a configuration that best meets your needs.
        </Typography>
        <Formik<CompanyInfoFormValues>
          initialValues={{
            industries: signup.industries || [],
            hasExistingQms: signup.hasExistingQms || false,
            existingQmsInfo: signup.existingQmsInfo || '',
            migrateExistingQms: signup.migrateExistingQms || false,
            hasPolicies: signup.hasPolicies || false
          }}
          onSubmit={values => update(values)}
        >
          {formikProps => (
            <CompanyInfoForm {...formikProps} onContinue={onContinue} />
          )}
        </Formik>
      </CardContent>
    </StyledCard>
  );
};

export default OnboardingCompanyInfo;

interface CompanyInfoFormProps extends FormikProps<CompanyInfoFormValues> {
  onContinue?: () => void;
}

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = props => {
  const { values, isValid, onContinue, handleChange, submitForm } = props;

  // override handlers to automatically submit the form
  const handleBlur = (event: React.FocusEvent<any>) => {
    submitForm();
    props.handleBlur(event);
  };
  const setFieldValue: typeof props.setFieldValue = (...args) => {
    submitForm();
    props.setFieldValue(...args);
  };
  const isCompleted =
    values.industries.length > 0 &&
    (!values.hasExistingQms || !!values.existingQmsInfo);

  return (
    <Form className='form'>
      <div className='field'>
        <Typography className='field-label'>Industry</Typography>
        <Typography className='caption'>
          What industry are you focused on (Pick all that apply).
        </Typography>
        <FormGroup>
          {industries.map(industry => (
            <FormControlLabel
              key={industry}
              control={
                <Checkbox
                  color='primary'
                  checked={values.industries.includes(industry)}
                  onChange={() => {
                    const industries = Array.from(values.industries);
                    const index = industries.indexOf(industry);
                    if (index > -1) {
                      industries.splice(index, 1);
                    } else {
                      industries.push(industry);
                    }
                    setFieldValue('industries', industries, true);
                  }}
                />
              }
              label={industry}
            />
          ))}
        </FormGroup>
      </div>
      <div className='field'>
        <Typography className='field-label'>
          Do you have an existing Quality Management System (QMS)?
        </Typography>
        <Typography className='caption'>
          We would like to learn more about what tools you currently use for
          your quality practices and compliance.
        </Typography>
        <FormControl name='hasExistingQms' component='fieldset'>
          <RadioGroup
            row
            value={values.hasExistingQms}
            onChange={event => {
              setFieldValue(
                'hasExistingQms',
                event.target.value === 'true',
                true
              );
              setFieldValue('existingQmsInfo', '', true);
            }}
          >
            {[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ].map((option, index) => (
              <FormControlLabel
                key={index}
                value={option.value}
                control={<Radio color='primary' />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
      {values.hasExistingQms && (
        <>
          <div className='field'>
            <Typography className='field-label'>
              What QMS are you currently using?
            </Typography>
            <Typography className='caption'>
              We would like to learn more about what tools you currently use for
              your quality practices and compliance.
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              className='form-field'
              name='existingQmsInfo'
              placeholder='Describe your existing QMS'
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.existingQmsInfo}
            />
          </div>
          <div className='field'>
            <Typography className='field-label'>
              Do you need help migrating data from your existing QMS?
            </Typography>
            <Typography className='caption'>
              We can help you migrate data from your existing QMS.
            </Typography>
            <FormControl name='migrateExistingQms' component='fieldset'>
              <RadioGroup
                row
                value={values.migrateExistingQms}
                onChange={event => {
                  setFieldValue(
                    'migrateExistingQms',
                    event.target.value === 'true',
                    true
                  );
                }}
              >
                {[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ].map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    control={<Radio color='primary' />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>
        </>
      )}
      <div className='field'>
        <Typography className='field-label'>
          Do you already have policies and procedures defined?
        </Typography>
        <Typography className='caption'>
          We can provide templates to get you started or adapt your current
          policies and procedures into the Sierra QMS.
        </Typography>
        <FormControl name='hasPolicies' component='fieldset'>
          <RadioGroup
            row
            value={values.hasPolicies}
            onChange={event => {
              setFieldValue('hasPolicies', event.target.value === 'true', true);
            }}
          >
            {[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ].map((option, index) => (
              <FormControlLabel
                key={index}
                value={option.value}
                control={<Radio color='primary' />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
      <Button
        type='submit'
        variant='contained'
        color='primary'
        size='large'
        className='submit'
        disabled={!isValid || !isCompleted}
        onClick={() => {
          if (isValid) {
            onContinue?.();
          }
        }}
      >
        Continue
      </Button>
    </Form>
  );
};
